/**
 * Relatório de vendas em CSV: 1 linha por item do pedido.
 * Totais do pedido (bruto / líquido esperado) só na 1ª linha de cada pedido.
 */

export const ORDERS_SALES_REPORT_HEADERS = [
    'pedido_id',
    'data_venda',
    'cliente',
    'forma_pagamento',
    'canal',
    'pedido_bruto',
    'pedido_liquido_esperado',
    'produto_id',
    'produto_nome',
    'qtd',
    'valor_unitario',
    'valor_linha',
    'custo_unitario',
    'custo_linha',
];

function asNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

/** Centavos → string pt-BR sem símbolo (ex.: 2500 → "25,00") para Excel. */
export function formatCentsForCsv(cents) {
    if (cents === '' || cents == null) {
        return '';
    }
    const n = asNumber(cents, 0) / 100;
    return n.toFixed(2).replace('.', ',');
}

function escapeCsvCell(value) {
    const str = value == null ? '' : String(value);
    if (/[;"\n\r]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

function formatSaleDate(value) {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
}

function paymentMethodsLabel(order) {
    const methods = order?.payment_methods || [];
    const names = methods.map((m) => m?.name).filter(Boolean);
    return names.length ? names.join(' + ') : '';
}

function productId(product) {
    return product?.id || product?.uuid || product?.product_uuid || '';
}

function lineUnitPriceCents(product) {
    if (product?.base_price != null) {
        return asNumber(product.base_price, 0);
    }
    if (product?.selling_price != null) {
        return asNumber(product.selling_price, 0);
    }
    return 0;
}

function lineTotalCents(product) {
    if (product?.total != null) {
        return asNumber(product.total, 0);
    }
    return lineUnitPriceCents(product) * asNumber(product?.quantity, 0);
}

function lineUnitCostCents(product) {
    return asNumber(product?.cost_price, 0);
}

/**
 * Achata pedidos em linhas de relatório (objetos com chaves = headers).
 */
export function flattenOrdersForSalesReport(orders) {
    const rows = [];
    const list = Array.isArray(orders) ? orders : [];

    for (const order of list) {
        const products = Array.isArray(order?.products) && order.products.length > 0
            ? order.products
            : [null];

        const orderGross = asNumber(order?.amount, 0);
        const orderNet = order?.expected_net != null
            ? asNumber(order.expected_net, orderGross)
            : orderGross;

        products.forEach((product, index) => {
            const isFirst = index === 0;
            const qty = product ? asNumber(product.quantity, 0) : 0;
            const unit = product ? lineUnitPriceCents(product) : 0;
            const lineTotal = product ? lineTotalCents(product) : 0;
            const unitCost = product ? lineUnitCostCents(product) : 0;
            const lineCost = unitCost * qty;

            rows.push({
                pedido_id: order?.id || '',
                data_venda: formatSaleDate(order?.created_at),
                cliente: order?.customer?.name || '',
                forma_pagamento: paymentMethodsLabel(order),
                canal: order?.sales_channel?.name || '',
                pedido_bruto: isFirst ? formatCentsForCsv(orderGross) : '',
                pedido_liquido_esperado: isFirst ? formatCentsForCsv(orderNet) : '',
                produto_id: product ? productId(product) : '',
                produto_nome: product?.name || '',
                qtd: product ? String(qty) : '',
                valor_unitario: product ? formatCentsForCsv(unit) : '',
                valor_linha: product ? formatCentsForCsv(lineTotal) : '',
                custo_unitario: product ? formatCentsForCsv(unitCost) : '',
                custo_linha: product ? formatCentsForCsv(lineCost) : '',
            });
        });
    }

    return rows;
}

export function buildOrdersSalesReportCsv(orders) {
    const rows = flattenOrdersForSalesReport(orders);
    const lines = [
        ORDERS_SALES_REPORT_HEADERS.join(';'),
        ...rows.map((row) => (
            ORDERS_SALES_REPORT_HEADERS.map((key) => escapeCsvCell(row[key])).join(';')
        )),
    ];
    return `\uFEFF${lines.join('\n')}\n`;
}

export function downloadOrdersSalesReportCsv(orders, filename) {
    const csv = buildOrdersSalesReportCsv(orders);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = filename || `relatorio-vendas-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export async function fetchAllOrdersForReport(api, { q, from, to, salesChannelId } = {}) {
    const limit = 100;
    let page = 1;
    let totalPages = 1;
    const items = [];

    while (page <= totalPages) {
        const params = new URLSearchParams({
            page: String(page),
            limit: String(limit),
        });
        if (q) {
            params.set('q', q);
        }
        if (from) {
            params.set('from', from);
        }
        if (to) {
            params.set('to', to);
        }
        if (salesChannelId) {
            params.set('sales_channel_id', salesChannelId);
        }

        const response = await api.get(`orders?${params.toString()}`);
        const data = response.data || {};
        const list = Array.isArray(data.orders) ? data.orders : [];
        items.push(...list);
        totalPages = Number(data.total_pages) || 1;
        page += 1;
        if (page > 100) {
            break;
        }
    }

    return items;
}
